import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { BarChart3 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const PIE_COLORS = ['#F59E0B', '#3B82F6', '#10B981', '#8B5CF6', '#EF4444', '#EC4899', '#06B6D4', '#F97316'];

export default function AdminAnalytics() {
  const { token, API } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAnalytics = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/analytics`, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [API, token]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) return <p className="text-zinc-500">Failed to load analytics</p>;

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-[#1C1C1E] border border-white/10 rounded-lg px-3 py-2 text-xs shadow-xl">
          <p className="text-zinc-400 mb-1">{label}</p>
          {payload.map((p, i) => (
            <p key={i} className="font-mono font-bold" style={{ color: p.color || '#F59E0B' }}>
              {p.name}: {p.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const actionLabels = {
    welcome: 'Welcome Bonus',
    new_creator: 'New Creator',
    link_protect: 'Link Protect',
    admin_adjust: 'Admin Adjust',
    daily: 'Daily Bonus',
  };

  return (
    <div className="space-y-8">
      {/* Points Distribution by Action */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="glass-card rounded-xl p-6">
          <h3 className="font-heading font-bold text-sm text-white mb-4">Points by Action Type</h3>
          {data.action_breakdown.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.action_breakdown.map(a => ({ ...a, label: actionLabels[a.action] || a.action }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#71717A' }} />
                <YAxis tick={{ fontSize: 10, fill: '#71717A' }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="total_points" fill="#F59E0B" radius={[4, 4, 0, 0]} name="Points" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-60 text-zinc-600 text-sm">No data</div>
          )}
        </div>

        {/* Action Count Pie */}
        <div className="glass-card rounded-xl p-6">
          <h3 className="font-heading font-bold text-sm text-white mb-4">Action Distribution</h3>
          {data.action_breakdown.length > 0 ? (
            <div className="flex items-center gap-6">
              <ResponsiveContainer width="50%" height={220}>
                <PieChart>
                  <Pie
                    data={data.action_breakdown}
                    dataKey="count"
                    nameKey="action"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    innerRadius={40}
                  >
                    {data.action_breakdown.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2 flex-1">
                {data.action_breakdown.map((a, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                    <span className="text-xs text-zinc-400 flex-1">{actionLabels[a.action] || a.action}</span>
                    <span className="text-xs font-mono text-zinc-300">{a.count}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-60 text-zinc-600 text-sm">No data</div>
          )}
        </div>
      </div>

      {/* Level Distribution */}
      <div className="glass-card rounded-xl p-6">
        <h3 className="font-heading font-bold text-sm text-white mb-4">User Level Distribution</h3>
        {data.level_distribution.length > 0 ? (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data.level_distribution}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
              <XAxis dataKey="level" tick={{ fontSize: 10, fill: '#71717A' }} label={{ value: 'Level', position: 'insideBottom', offset: -5, fill: '#71717A', fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10, fill: '#71717A' }} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" fill="#8B5CF6" radius={[4, 4, 0, 0]} name="Users" />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center h-40 text-zinc-600 text-sm">No data</div>
        )}
      </div>

      {/* Hourly Activity (24h) */}
      <div className="glass-card rounded-xl p-6">
        <h3 className="font-heading font-bold text-sm text-white mb-4">Activity Last 24 Hours</h3>
        {data.hourly_activity.length > 0 ? (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data.hourly_activity}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
              <XAxis dataKey="hour" tick={{ fontSize: 9, fill: '#71717A' }} tickFormatter={v => v.slice(11) + ':00'} />
              <YAxis tick={{ fontSize: 10, fill: '#71717A' }} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" fill="#10B981" radius={[4, 4, 0, 0]} name="Actions" />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center h-40 text-zinc-600 text-sm">No activity in last 24h</div>
        )}
      </div>
    </div>
  );
}
